import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  callWithAuth,
  getActiveCourseFilters,
  putActiveCourseFilters,
  Filters,
} from "@/api/Api";

export const useActiveCourseFilters = () => {
  const queryClient = useQueryClient();

  const filtersQuery = useQuery({
    queryKey: ["activeCourseFilters"],
    queryFn: () => callWithAuth(getActiveCourseFilters),
  });

  const updateFiltersMutation = useMutation({
    mutationFn: (data: Filters) => putActiveCourseFilters(data),
    // Optimistically apply the new filters so cycling the level pill feels instant
    // (mirrors iOS useActiveCourseFilters onMutate).
    onMutate: (variables) => {
      const previous = queryClient.getQueryData<Filters>([
        "activeCourseFilters",
      ]);
      queryClient.setQueryData(["activeCourseFilters"], variables);
      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["activeCourseFilters"], context.previous);
      }
    },
    onSettled: () => {
      filtersQuery.refetch();
    },
  });

  const setFilters = (filters: Filters) => {
    updateFiltersMutation.mutate(filters);
  };

  const clearFilters = () => {
    updateFiltersMutation.mutate({
      selectedLevel: 0,
      selectedTopics: [],
    });
  };

  return {
    selectedLevel: filtersQuery.data?.selectedLevel ?? 0,
    selectedTopics: filtersQuery.data?.selectedTopics ?? [],
    setFilters,
    clearFilters,
    isLoading: filtersQuery.isLoading,
  };
};
